/**
 * Defines group hierarchy templates for database bootstrapping.
 *
 * Each template is a tree of {@link GroupNode} objects describing
 * the groups to create under CompanyGroup.  Presets select subsets
 * of these templates so different database types get different groups.
 */

/** A node in the group tree. `children` are created beneath it. */
export interface GroupNode {
  /** Display name of the group in Geotab. */
  name: string;
  /** Optional child groups. */
  children?: GroupNode[];
}

/** A named collection of {@link GroupNode} trees. */
export interface GroupTemplate {
  /** Unique identifier for this template. */
  id: string;
  /** Human-readable label (used in UI). */
  label: string;
  /** Root-level nodes to create under CompanyGroup. */
  nodes: GroupNode[];
}

/** A preset selects a subset of templates to apply. */
export interface GroupPreset {
  /** Unique identifier. */
  id: string;
  /** Human-readable label. */
  label: string;
  /** IDs of templates included in this preset. */
  templateIds: string[];
}

// ---------------------------------------------------------------------------
// Template definitions
// ---------------------------------------------------------------------------

function yearChildren(from: number, to: number): GroupNode[] {
  const children: GroupNode[] = [];
  for (let y = from; y <= to; y++) {
    children.push({ name: String(y) });
  }
  return children;
}

export const GROUP_TEMPLATES: GroupTemplate[] = [
  {
    id: "fairfleet-services",
    label: "Fairfleet Services",
    nodes: [
      {
        name: "Fairfleet Services",
        children: [
          { name: "VehicleStatus" },
        ],
      },
    ],
  },
  {
    id: "kraftstoff",
    label: "02 Kraftstoff",
    nodes: [
      {
        name: "100 Assetinformation",
        children: [
          {
            name: "02 Kraftstoff",
            children: [
              { name: "01 Diesel" },
              { name: "02 Elektro" },
              { name: "03 CNG" },
              { name: "04 LPG" },
              { name: "06 PHEV" },
              { name: "07 Fuel Cell" },
              { name: "08 Benziner" },
              { name: "99 other" },
              { name: "nicht nutzen" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "baujahr",
    label: "04 Baujahr",
    nodes: [
      {
        name: "100 Assetinformation",
        children: [
          {
            name: "04 Baujahr",
            children: yearChildren(2010, 2026),
          },
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

export const GROUP_PRESETS: GroupPreset[] = [
  {
    id: "standard",
    label: "Standard",
    templateIds: ["fairfleet-services", "kraftstoff", "baujahr"],
  },
];
